<template>
    <v-container class="pa-5" fluid>
        <PersonalPickupOrdersDialog
            :is-open="personalPickupsOrdersDialog"
            :action="action"
            :personal-pickup-orders-list-passed="personalPickupOrders"
            :personal-pickup-data="personalPickup"
            @on-save="savePersonalPickupOrders"
            @on-close="closePersonalPickupsOrdersDialog"
        />
        <ConfirmDialog
            :is-open="updatePersonalPickupDialog"
            :data="personalPickup"
            :action-loading="updatePersonalPickupLoading"
            @on-confirm="updatePersonalPickup"
            @on-abort="closeUpdatePersonalPickupDialog"
        >
            <template #content>
                Czy na pewno chcesz potwierdzić odbiór osobisty?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>
        <page-title>
            {{ pageTitle }}
        </page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container class="pa-0">
                    <v-row v-if="personalPickup && !isNewPersonalPickup">
                        <v-col cols="12" lg="6" class="d-flex">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <v-card>
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Dane odbioru osobistego</v-toolbar-title>
                                                <v-spacer />
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <div v-on="on">
                                                            <v-btn
                                                                v-if="personalPickup.previewUrl"
                                                                icon
                                                                data-sentry-label="Podgląd WZ"
                                                                :disabled="blockPrintPreview"
                                                                @click="showPersonalPickupPreview(personalPickup.previewUrl)"
                                                            >
                                                                <v-icon color="blue">
                                                                    mdi-printer
                                                                </v-icon>
                                                            </v-btn>
                                                        </div>
                                                    </template>
                                                    <span v-if="blockPrintPreview">Przed wydrukiem zapisz zmiany</span>
                                                    <span v-else>Drukuj</span>
                                                </v-tooltip>
                                            </v-toolbar>
                                            <v-divider />
                                            <v-card-text>
                                                <v-container class="pa-0">
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <table class="table table--simple">
                                                                <tbody>
                                                                    <tr>
                                                                        <th>Numer:</th>
                                                                        <td>{{ personalPickup.number }}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th>Utworzony przez:</th>
                                                                        <td v-if="personalPickup.createdBy.firstName && personalPickup.createdBy.lastName">
                                                                            {{ personalPickup.createdBy.firstName }} {{ personalPickup.createdBy.lastName }}
                                                                        </td>
                                                                        <td v-else>
                                                                            {{ personalPickup.username }}
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <th>Data utworzenia:</th>
                                                                        <td>{{ formatDate(personalPickup.createdAt) }}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                    <v-row v-if="isNewPersonalPickup || personalPickup.statusId === 10" align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Dodaj zamówienie na WZ
                            </h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="secondary"
                                fab
                                small
                                class="elevation-1"
                                data-sentry-label="Dodaj zamówienie do WZ"
                                @click="showPersonalPickupsOrdersDialog()"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-toolbar v-if="!isNewPersonalPickup" flat dense color="white lighten-2">
                                    <v-toolbar-title>Zamówienia na WZ</v-toolbar-title>
                                </v-toolbar>
                                <v-divider v-if="!isNewPersonalPickup" />
                                <v-card-text v-if="personalPickupOrders.length > 0">
                                    <v-data-table
                                        :headers="headers"
                                        :items="personalPickupOrders"
                                        :mobile-breakpoint="0"
                                        disable-pagination
                                        hide-default-footer
                                        name="personalPickupsOrdersData"
                                    >
                                        <template #item.shopOrderNumer="{item}">
                                            <template v-if="item.shopOrderNumer">
                                                {{ item.shopOrderNumer }}
                                            </template>
                                            <template v-else>
                                                -
                                            </template>
                                        </template>
                                        <template #item.productionNumber="{item}">
                                            <template v-if="item.productionNumber">
                                                {{ item.productionNumber }}
                                            </template>
                                            <template v-else>
                                                -
                                            </template>
                                        </template>
                                        <template #item.firstProductName="{item}">
                                            <template v-if="item.firstProductName">
                                                {{ item.firstProductName }}
                                            </template>
                                            <template v-else>
                                                -
                                            </template>
                                        </template>
                                        <template #item.amount="{item}">
                                            <template v-if="isNewPersonalPickup || !item.id">
                                                <v-edit-dialog
                                                    ref="AmountEditDialog"
                                                    large
                                                    persistent
                                                    cancel-text="Anuluj"
                                                    save-text="Zapisz"
                                                    @save="saveAmount(item)"
                                                    @close="closeSaveAmount(item)"
                                                    @cancel="cancel = true"
                                                >
                                                    <div class="d-inline-flex">
                                                        {{ item.amount }}
                                                        <v-icon color="primary" small class="ml-1">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </div>
                                                    <template #input>
                                                        <v-text-field
                                                            v-model.number="item.amount"
                                                            label="Nakład"
                                                            required
                                                            :error="amountErrors(item)"
                                                        />
                                                    </template>
                                                </v-edit-dialog>
                                            </template>
                                            <template v-else>
                                                {{ item.amount }}
                                            </template>
                                        </template>
                                        <template #item.packageAmount="{item}">
                                            <template v-if="isNewPersonalPickup || !item.id">
                                                <v-edit-dialog
                                                    ref="packageEditDialog"
                                                    large
                                                    persistent
                                                    cancel-text="Anuluj"
                                                    save-text="Zapisz"
                                                    @open="editPackageEditDialog(item)"
                                                    @close="savePackageEditDialog(item)"
                                                    @cancel="cancel = true"
                                                >
                                                    <div class="d-inline-flex">
                                                        {{ item.packageAmount }}
                                                        <v-icon color="primary" small class="ml-1">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </div>
                                                    <template #input>
                                                        <v-text-field
                                                            v-model.number="item.packageAmount"
                                                            label="Kartony"
                                                            required
                                                            :error="packageEditErrors(item)"
                                                        />
                                                    </template>
                                                </v-edit-dialog>
                                            </template>
                                            <template v-else>
                                                {{ item.packageAmount }}
                                            </template>
                                        </template>
                                        <template #item.isPartialPickup="{item}">
                                            <template>
                                                <v-icon
                                                    v-if="item.isPartialPickup"
                                                    small
                                                    color="success"
                                                >
                                                    mdi-check
                                                </v-icon>
                                                <v-icon v-else small color="error">
                                                    mdi-close
                                                </v-icon>
                                            </template>
                                        </template>
                                        <template v-if="isNewPersonalPickup || personalPickup.statusId === 10" #item.actions="{item}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń"
                                                            v-on="on"
                                                            @click="deletePersonalPickupOrder(item.order)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card-text>

                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                        class="ma-0"
                                    >
                                        Brak zamówień na WZ.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                v-if="isNewPersonalPickup || personalPickup.statusId === 10"
                                color="success"
                                medium
                                class="mr-3"
                                data-sentry-label="Zapisz"
                                :loading="actionLoading"
                                :disabled="personalPickupOrders.length === 0 && action === 'createPersonalPickup'"
                                @click="savePersonalPickup"
                            >
                                Zapisz
                            </v-btn>
                            <v-btn
                                v-if="personalPickup.statusId === 10 && action === 'updatePersonalPickup'"
                                color="primary"
                                medium
                                class="mr-3"
                                data-sentry-label="Potwierdź"
                                :loading="updatePersonalPickupLoading"
                                :disabled="personalPickupOrders.length === 0"
                                @click="showUpdatePersonalPickupDialog"
                            >
                                Potwierdź WZ
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { isEmpty } from 'lodash'
import store from '@/./store'
import { required } from 'vuelidate/lib/validators'
import { errorHandler } from '@/utils/errorHandler'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import { apiConfig } from '@/config'
import PageTitle from '@/components/common/PageTitle'
import PersonalPickupOrdersDialog from './PersonalPickupOrdersDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'

export default {
    name: 'PersonalPickup',
    components: {
        PageTitle,
        PersonalPickupOrdersDialog,
        ConfirmDialog
    },
    mixins: [ tooltipDelay, dateFormatter ],
    beforeRouteEnter(to, from, next) {
        if (typeof to.params.id !== 'undefined') {
            store.dispatch('shipments/personalPickups/getPersonalPickup', to.params.id)
                .then(response => {
                    next(vm => {
                        vm.action = 'updatePersonalPickup'
                        vm.personalPickup = Object.assign({}, response.data)
                        vm.personalPickupOrders = vm.personalPickup.personalPickupOrders
                        vm.initialPersonalPickupOrdersAmount = vm.personalPickup.personalPickupOrders.length
                        vm.pageTitle = `Odbiór osobisty - ${response.data.number}`
                        if ((from.name === 'shipmentsPersonalPickups' || !from.name) && vm.personalPickup.statusId === 10) {
                            vm.headers.push({
                                text: 'Akcje',
                                value: 'actions',
                                align: 'center',
                                sortable: false,
                                width: 100
                            })
                        }
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        } else {
            next(vm => {
                vm.action = 'createPersonalPickup'
                vm.pageTitle = 'Tworzenie odbioru osobistego'
                vm.headers.push({
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                })
            })
        }
    },
    data() {
        return {
            pageTitle: null,
            personalPickup: {},
            personalPickupToUpdate: {},
            personalPickupsOrdersDialog: false,
            personalPickupOrders: [],
            personalPickupOrder: {},
            filePreviewURI: null,
            actionLoading: false,
            action: null,
            imagePreviewDialog: false,
            updatePersonalPickupLoading: false,
            updatePersonalPickupDialog: false,
            cancel: false,
            blockPrintPreview: false,
            initialPersonalPickupOrdersAmount: null,
            headers: [
                {
                    text: 'Numer zamówienia klienta',
                    value: 'shopOrderNumber',
                    sortable: false,
                },
                {
                    text: 'Numer zamówienia',
                    value: 'productionNumber',
                    sortable: false,
                },
                {
                    text: 'Produkt',
                    value: 'firstProductName',
                    sortable: false,
                },
                {
                    text: 'Nakład odebrany',
                    value: 'amount',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Ilość kartonów',
                    value: 'packageAmount',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Odbiór częściowy',
                    value: 'isPartialPickup',
                    sortable: false,
                    align: 'center',
                    width: 200
                },
            ]
        }
    },
    validations() {
        return {
            personalPickupOrders: {
                $each: {
                    amount: {
                        required,
                        isValid: (value, personalPickupOrder) => {
                            return value > 0 && value <= personalPickupOrder.initialAmount
                        }
                    },
                    packageAmount: {
                        required,
                        isValid: (value) => {
                            return value > 0
                        }
                    }
                }
            },
        }
    },
    computed: {
        isNewPersonalPickup() {
            return isEmpty(this.personalPickup)
        },
    },
    methods: {
        showPersonalPickupPreview(pdfFile) {
            const file = this.taskPreviewURI(pdfFile)

            window.open(file, '_blank')
        },
        taskPreviewURI(image) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            return `${mediaRoot}${image}`
        },
        showPersonalPickupsOrdersDialog() {
            this.personalPickupsOrdersDialog = true
        },
        closePersonalPickupsOrdersDialog() {
            this.personalPickupsOrdersDialog = false
        },
        savePersonalPickupOrders(personalPickupOrders) {
            personalPickupOrders.forEach(personalPickupOrder => {
                const isPersonalPickupOrderAdded = this.personalPickupOrders.some(item => item.order === personalPickupOrder.id)

                if (!isPersonalPickupOrderAdded) {
                    this.personalPickupOrders.push({
                        shopOrderNumber: personalPickupOrder.shopOrderNumber,
                        amount: personalPickupOrder.quantityLeft,
                        order: personalPickupOrder.id,
                        isPartialPickup: false,
                        productionNumber: personalPickupOrder.productionNumber,
                        firstProductName: personalPickupOrder.firstProductName,
                        initialAmount: personalPickupOrder.quantityLeft,
                        savedAmount: null,
                        customer: personalPickupOrder.customer,
                        packageAmount: 0
                    })
                }
                if (this.action === 'updatePersonalPickup') {
                    this.blockPrintPreview = this.personalPickupOrders.some(item => item.id === undefined)
                }
            })

        },
        deletePersonalPickupOrder(order) {
            const index = this.personalPickupOrders.findIndex(item => item.order === order)

            if (index > -1) this.personalPickupOrders.splice(index, 1)
            if (this.action === 'updatePersonalPickup') this.blockPrintPreview = this.initialPersonalPickupOrdersAmount !== this.personalPickupOrders.length ? true : false
        },
        savePersonalPickup() {
            this.actionLoading = true

            let payload = null

            this.isNewPersonalPickup ? payload = { personalPickupOrders: this.personalPickupOrders } : payload = this.personalPickup

            this.$store.dispatch(`shipments/personalPickups/${this.action}`, payload)
                .then(response => {
                    if (response.data.id) {
                        this.personalPickup = Object.assign({}, response.data)
                        this.personalPickupOrders = this.personalPickup.personalPickupOrders
                        this.initialPersonalPickupOrdersAmount = this.personalPickup.personalPickupOrders.length
                        this.blockPrintPreview = false
                    }
                    if (response.data.personalPickupIds) {
                        const route = response.data.personalPickupIds.length === 1 ? {
                            name: 'shipmentsPersonalPickupsPersonalPickup',
                            params: { id: response.data.personalPickupIds[0]}
                        } : { name: 'shipmentsPersonalPickups'}

                        this.$router.push(route)
                            .catch(error => {
                                errorHandler(error)
                            })
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        showUpdatePersonalPickupDialog() {
            this.personalPickupToUpdate = this.personalPickup
            this.updatePersonalPickupDialog = true
        },
        updatePersonalPickup(personalPickup) {
            this.updatePersonalPickupLoading = true
            personalPickup.statusId = 20
            this.$store.dispatch('shipments/personalPickups/updatePersonalPickup', personalPickup)
                .then(response => {
                    this.personalPickup = Object.assign({}, response.data)
                    this.personalPickupOrders = this.personalPickup.personalPickupOrders
                    this.initialPersonalPickupOrdersAmount = this.personalPickup.personalPickupOrders.length
                    const index = this.headers.findIndex(item => item.text === 'Akcje')

                    if (index > -1) this.headers.splice(index, 1)
                    this.blockPrintPreview = false
                })
                .catch(error => {
                    store.dispatch('shipments/personalPickups/getPersonalPickup', personalPickup.id)
                        .then(response => {
                            this.personalPickup = Object.assign({}, response.data)
                            this.personalPickupOrders = this.personalPickup.personalPickupOrders
                            this.initialPersonalPickupOrdersAmount = this.personalPickup.personalPickupOrders.length
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeUpdatePersonalPickupDialog()
                })
        },
        closeUpdatePersonalPickupDialog() {
            this.updatePersonalPickupLoading = false
            this.updatePersonalPickupDialog = false

            setTimeout(() => {
                this.personalPickupToUpdate = {}
            }, 500)
        },
        saveAmount(item) {
            this.$v.$touch()
            const index = this.personalPickupOrders.findIndex(el => el.order === item.order)

            if (!this.$v.personalPickupOrders.$each.$iter[index].amount.required) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę uzupełnić nakład.'
                })

                return
            }
            if (!this.$v.personalPickupOrders.$each.$iter[index].amount.isValid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: `Proszę podać prawidłową wartość z zakresu 1-${this.personalPickupOrders[index].initialAmount}`
                })

                return
            }
            this.personalPickupOrders[index].savedAmount = item.amount
            this.personalPickupOrders[index].isPartialPickup = item.amount !== this.personalPickupOrders[index].initialAmount
            this.personalPickupOrders.splice(index, 1, item)
        },
        closeSaveAmount(item) {
            const index = this.personalPickupOrders.findIndex(el => el.order === item.order)

            if (this.cancel) {
                this.$v.$reset()
                this.personalPickupOrders[index].amount = this.personalPickupOrders[index].savedAmount || this.personalPickupOrders[index].initialAmount
                this.personalPickupOrders[index].isPartialPickup = item.amount !== this.personalPickupOrders[index].initialAmount
            } else {
                if (!this.$v.personalPickupOrders.$each.$iter[index].amount.required || !this.$v.personalPickupOrders.$each.$iter[index].amount.isValid) {
                    this.$refs.AmountEditDialog.isActive = true
                } else {
                    this.$v.$reset()
                    this.$refs.AmountEditDialog.isActive = false
                } 
            }
            this.cancel = false
        },
        savePackageEditDialog(item) {
            this.$v.$touch()
            const index = this.personalPickupOrders.findIndex(el => el.order === item.order)
            
            if (this.cancel) {
                item.packageAmount = item.initialpackageAmount
                this.cancel = false

                return
            } 
            
            if (!this.$v.personalPickupOrders.$each.$iter[index].packageAmount.isValid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę podać co najmniej jeden karton'
                })
                this.$refs.packageEditDialog.isActive = true
            }
            if (item.packageAmount === '') {
                item.packageAmount = item.initialpackageAmount
            }
        },
        editPackageEditDialog(item) {
            item.initialpackageAmount = item.packageAmount
        },
        close() {
            this.$router.push({ name: 'shipmentsPersonalPickups'})
                .catch(error => {
                    errorHandler(error)
                })
        },
        amountErrors(item) {
            const index = this.personalPickupOrders.findIndex(el => el.order === item.order)

            if (!this.$v.personalPickupOrders.$each.$iter[index].amount.$dirty) return

            return !this.$v.personalPickupOrders.$each.$iter[index].amount.required || !this.$v.personalPickupOrders.$each.$iter[index].amount.isValid
        },
        packageEditErrors(item) {
            const index = this.personalPickupOrders.findIndex(el => el.order === item.order)

            return !this.$v.personalPickupOrders.$each.$iter[index].packageAmount.required || !this.$v.personalPickupOrders.$each.$iter[index].packageAmount.isValid
        },
    },
}
</script>